import { Button } from 'components/Button';
import CompactNavbar from 'components/CompactNavbar/CompactNavbar.component';
import ExpandedNavBar from 'components/ExpandedNavbar/ExpandedNavbar.component';
import Icon from 'components/Icon/Icon.component';
import { WalletDropdownPanel } from 'components/SignedInUserToolbar/partials/WalletDropdownPanel.component';
import SignedInUserToolbar from 'components/SignedInUserToolbar/SignedInUserToolbar.component';
import SlideInMenu from 'components/SlideInMenu/SlideInMenu.component';
import { SlideInMenuVariant } from 'components/SlideInMenu/SlideInMenu.config';
import { MAIN_SITE_HOMEPAGE } from 'config';
import ChevronLeft from 'images/icons/chevron-left.svg?react';
import { useEffect, useState } from 'react';
import { Translate } from 'react-localize-redux';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { requestDataIfNecessary } from 'services/api/api.actions';
import {
  selectNavbarContent,
  selectNavbarContentApiConfig,
} from 'services/cmsContent/cmsContent.selectors';
import { useI18n } from 'services/i18n/i18n.hooks';
import { logoutRequest } from 'services/session/session.actions';
import {
  selectIsSessionValidated,
  selectSessionInfo,
} from 'services/session/session.selectors';
import { selectUserBalance } from 'services/user/user.selectors';
import { useIsAtLeastDesktopBreakpoint } from 'utils/hooks/media-queries';
import { useBodyScrollLock } from 'utils/hooks/useBodyScrollLock';
import { useIsApp } from 'utils/hooks/useGetIsApp';
import { LogoutBtn } from './components/logout-btn';
import NotSignedInUserFallback from './partials/NotSignedUserFallback.component';
import {
  AppHeading,
  DropdownShadow,
  SiteSection,
  TopBarRoot,
} from './style/Navbar.style';

export default function NavbarWithBonusBalance() {
  const { getState } = useStore();
  const dispatch = useDispatch();

  const navbarContent = useSelector(selectNavbarContent);
  const isAtLeastDesktopBreakpoint = useIsAtLeastDesktopBreakpoint();
  const isUserLoggedIn = useSelector(selectIsSessionValidated);
  const userBalance = useSelector(selectUserBalance);

  const isApp = useIsApp();

  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  useBodyScrollLock(isExpanded && !isAtLeastDesktopBreakpoint);

  const sessionInfo = selectSessionInfo(getState());
  const { channel } = sessionInfo;

  useEffect(() => {
    const apiConfig = selectNavbarContentApiConfig(getState());
    dispatch(requestDataIfNecessary(apiConfig));
  }, [dispatch, getState]);

  const realBonus = userBalance.bonusTypeBalances?.find(
    ({ bonusType }) => bonusType === 'BS',
  )?.balance;
  const {
    formatters: { formatCurrency },
  } = useI18n();

  const handleLogoutButtonClick = () => {
    dispatch(logoutRequest());
  };

  const handleGoBackButton = () => {
    window.location.assign(MAIN_SITE_HOMEPAGE);
  };

  const formattedBonus = formatCurrency(realBonus ?? 0);
  const formattedBalance = formatCurrency(userBalance.realCashBalance, {
    removeWhiteSpaces: true,
  });

  if (isAtLeastDesktopBreakpoint) {
    return (
      <>
        {!isApp && (
          <TopBarRoot isOverlayActive={isDropdownOpen}>
            <ExpandedNavBar navbarContent={navbarContent}>
              {isUserLoggedIn ? (
                <SignedInUserToolbar
                  variant="expanded"
                  onDropdownToggle={setIsDropdownOpen}
                  onPanelToggle={setIsExpanded}
                  isPanelExpanded={isExpanded}
                />
              ) : (
                <NotSignedInUserFallback padding="0 16px" />
              )}
            </ExpandedNavBar>
            <SlideInMenu
              navbarContent={navbarContent}
              variant={SlideInMenuVariant.expanded}
            />
          </TopBarRoot>
        )}

        {isExpanded && (
          <>
            <DropdownShadow
              onClick={() => {
                setIsExpanded(false);
                setIsDropdownOpen(false);
              }}
            />
            <WalletDropdownPanel
              onClose={() => setIsExpanded(false)}
              formattedBalance={formattedBalance}
              formattedBonus={formattedBonus}
            />
          </>
        )}
      </>
    );
  }

  return (
    <TopBarRoot isOverlayActive={isDropdownOpen}>
      <CompactNavbar navbarContent={navbarContent} isOverlayActive={isExpanded}>
        {isUserLoggedIn ? (
          <SignedInUserToolbar
            variant="compact"
            onDropdownToggle={setIsDropdownOpen}
            onPanelToggle={setIsExpanded}
            isPanelExpanded={isExpanded}
          />
        ) : (
          <NotSignedInUserFallback />
        )}
      </CompactNavbar>

      {isExpanded && (
        <>
          <DropdownShadow
            onClick={() => {
              setIsExpanded(false);
              setIsDropdownOpen(false);
            }}
          />

          <WalletDropdownPanel
            onClose={() => {
              setIsExpanded(false);
              setIsDropdownOpen(false);
            }}
            formattedBalance={formattedBalance}
            formattedBonus={formattedBonus}
          />
        </>
      )}

      <SiteSection>
        <Button
          variant="ghost"
          onClick={handleGoBackButton}
          padding="0"
          height="25px"
          aria-label="previous page"
          style={{ visibility: isExpanded ? 'hidden' : 'visible' }}
        >
          <Icon svgIconComponent={ChevronLeft} fill="currentColor" />
        </Button>

        <AppHeading>
          <Translate id="lbl.mainHeading" />
        </AppHeading>

        {isUserLoggedIn && <LogoutBtn />}
      </SiteSection>

      <SlideInMenu
        navbarContent={navbarContent}
        variant={SlideInMenuVariant.compact}
      />
    </TopBarRoot>
  );
}
